import {
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Form,
  Input,
  ListButton,
  notification,
  Row,
  Select,
  Show,
  Space,
  Typography,
} from '@pankod/refine-antd';
import { useNavigation } from '@pankod/refine-core';
import { useParams } from '@pankod/refine-react-router-v6';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { IRole } from 'interfaces';
import { useEffect, useState } from 'react';
import { getRolePermissions, getRoles, updateRole, updateRolePermissions } from 'services/role';
import { IPermissionObject, populateRequiredPermissions } from 'utilities/permission';
import { capitalize } from 'utilities/shared';

const getPermissionsString = (permissions: any[]) => {
  return permissions.reduce((accumulator, permission) => {
    const { name, actions } = permission;
    Object.keys(actions).forEach((action) => {
      if (actions[action]) {
        accumulator.push(`${action}:${name}`);
      }
    });
    return accumulator;
  }, [] as string[]);
};

const { Text } = Typography;

export const RoleEdit = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [roles, setRoles] = useState<IRole[]>([]);
  const [option, setOption] = useState<{ label: string; value: string } | null>(null);
  const [isRolePermissionsFetching, setIsRolePermissionsFetching] = useState<boolean>(false);
  const [rolePermissions, setRolePermissions] = useState<Array<IPermissionObject>>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const params = useParams();
  const [form] = Form.useForm();
  const { edit, list } = useNavigation();

  useEffect(() => {
    async function fetchRolePermissions(roleId: string) {
      setIsRolePermissionsFetching(true);
      try {
        const response = await getRolePermissions(roleId);
        const populatedPermissions = await populateRequiredPermissions(response);
        setRolePermissions(populatedPermissions);
      } catch (err: any) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      }
      setIsRolePermissionsFetching(false);
    }

    async function fetchRoles() {
      setIsLoading(true);
      try {
        const response = await getRoles();
        setRoles(response);

        const currentOption = response.find((item) => item.id === params.id);
        if (currentOption) {
          setOption({ label: currentOption.name, value: currentOption.id });
          fetchRolePermissions(currentOption.id);
          form.setFieldsValue({ name: currentOption.name, description: currentOption.description });
        }
      } catch (err: any) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      }
      setIsLoading(false);
    }

    fetchRoles();

    return () => {
      setRoles([]);
      setRolePermissions([]);
    };
  }, [form, params.id]);

  const roleOptions = roles.map((item) => ({
    label: item.name,
    value: item.id,
  }));

  const onSelect = (option: any) => {
    const selectedOption = roleOptions.find((item) => item.value === option);
    if (selectedOption) {
      setOption(selectedOption);
      edit('roles', option);
    }
  };

  const onFinish = async (values: any) => {
    if (!option) return;

    setIsSubmitting(true);
    try {
      const roleId = option.value;
      const { name, description } = values;
      const permissionsString = getPermissionsString(rolePermissions);
      await Promise.all([
        updateRole(roleId, { name, description }),
        updateRolePermissions(roleId, permissionsString),
      ]);

      notification.success({
        message: 'Success',
        description: 'Successfully update permissions',
      });
      setTimeout(() => list('roles'), 1000);
    } catch (err) {
      notification.error({
        message: 'Failed',
        description: 'Failed to update permissions',
      });
    }
    setIsSubmitting(false);
  };

  const header = (
    <Space>
      <Space>
        <Text strong>Role</Text>
        <Select
          value={option}
          options={roleOptions}
          style={{ width: '200px' }}
          filterOption={false}
          onSelect={onSelect}
          loading={isLoading}
          disabled={isLoading}
        />
      </Space>

      <ListButton />
    </Space>
  );

  const updatePermission = (e: CheckboxChangeEvent, permissionName: string) => {
    const { value: permissionAction, checked: isChecked } = e.target;

    setRolePermissions((prevPermissions) => {
      const newPermissions = structuredClone(prevPermissions);
      const permission = newPermissions.find((p) => p.name === permissionName);

      if (permission) {
        permission.actions[permissionAction] = isChecked;
        if (permissionAction === 'view') {
          permission.actions['read'] = isChecked;
        }
      }

      return newPermissions;
    });
  };

  return (
    <Show title="Edit Role" headerButtons={header}>
      <Form
        name="dynamic_form_item"
        form={form}
        initialValues={{ permissions: rolePermissions }}
        onFinish={onFinish}
      >
        <Row justify="space-around" align="middle">
          <Col xs={24} lg={20}>
            <Card title="Information" loading={isLoading}>
              <Form.Item
                label="Role name"
                name="name"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Input placeholder="Enter role name" />
              </Form.Item>
              <Form.Item
                label="Description"
                name="description"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Input.TextArea rows={8} placeholder="Description" />
              </Form.Item>
            </Card>
          </Col>
        </Row>

        <Divider />

        <Card title="Permissions" loading={isRolePermissionsFetching || isLoading}>
          {rolePermissions.map((permission, idx) => {
            const { name, actions } = permission;

            return (
              <Card
                key={name}
                type="inner"
                title={name.toUpperCase()}
                extra={null}
                {...(idx !== 0 && { style: { marginTop: 16 } })}
              >
                {Object.keys(actions).map((action) => {
                  const isPermitted = actions[action];

                  return (
                    <Checkbox
                      key={`${name}:${action}`}
                      value={action}
                      checked={isPermitted}
                      onChange={(e) => updatePermission(e, name)}
                    >
                      {capitalize(action)}
                    </Checkbox>
                  );
                })}
              </Card>
            );
          })}
        </Card>

        <Form.Item wrapperCol={{ span: 4, offset: 22 }} style={{ marginTop: 16 }}>
          <Button disabled={isSubmitting} type="primary" htmlType="submit">
            Save
          </Button>
        </Form.Item>
      </Form>
    </Show>
  );
};
